import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  signInWithPopup, 
  getRedirectResult,
  OAuthProvider, 
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase';
import { GrainOverlay, Glow } from '@/components/landing/LandingShell';
import { LogoWithText } from '@/components/Logo';
import { isNativePlatform, isNativeIOS, signInWithAppleNative } from '../lib/appleNative';
import { ForgotPasswordDialog, ForgotUsernameHelpDialog } from '../components/AccountRecovery';
import {
  PRIVACY_VERSION,
  TERMS_VERSION,
  APP_VERSION,
} from '@/lib/legalVersions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Background and logo URLs (local assets for reliability)
const ZENSCROLL_LOGO = '/assets/splash/zenscroll-logo-transparent.png';

// Extract a readable error message from any axios/FastAPI response.
// FastAPI 422 responses have `detail` as an array of objects, which would
// otherwise render as "[object Object]" in a toast.
const getErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (first?.msg) {
      const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : null;
      return field ? `${field}: ${first.msg}` : first.msg;
    }
  }
  return error?.message || fallback;
};

// Detect iOS/iPadOS devices
const isIOSDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return true;
  }
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }
  return false;
};

export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSignUp = searchParams.get('mode') === 'signup';
  const showDiag = searchParams.get('diag') === '1';
  const [diagInfo, setDiagInfo] = useState(null);
  useEffect(() => {
    if (!showDiag) return;
    (async () => {
      try {
        const { nativeAuthDiagnostics } = await import('../lib/googleNative');
        setDiagInfo(nativeAuthDiagnostics());
      } catch (err) {
        setDiagInfo({ error: err?.message || 'diagnostics-import-failed' });
      }
    })();
  }, [showDiag]);
  const [loading, setLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState(null); // 'apple', 'google', or null for email
  const [authError, setAuthError] = useState(null);
  const [agreedToLegal, setAgreedToLegal] = useState(false);
  const [pendingOAuth, setPendingOAuth] = useState(null); // 'google' | 'apple' | null
  // Account-recovery modals — shown only on Sign In mode where native
  // email/password is on screen (Apple-only / Google-only flows hide them).
  const [recoverPwdOpen, setRecoverPwdOpen] = useState(false);
  const [recoverUserOpen, setRecoverUserOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    full_name: '',
    date_of_birth: '',
  });

  // Build the consent payload to send to the backend when an account is
  // (or may be) created. Returns null when we should not send consent yet.
  const buildConsentPayload = () => ({
    accepted_terms: true,
    accepted_privacy: true,
    accepted_at: new Date().toISOString(),
    terms_version: TERMS_VERSION,
    privacy_version: PRIVACY_VERSION,
    app_version: APP_VERSION,
  });

  // Hard gate: signup-mode flows must have the legal box checked.
  const requireConsent = () => {
    if (isSignUp && !agreedToLegal) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue.');
      return false;
    }
    return true;
  };

  const isIOS = isIOSDevice();
  // Platform-aware login screen selection:
  // - Native iOS Capacitor build: show Apple + Email only.
  // - Web / Android / any non-iOS-native context: show Google + Email only.
  const showAppleButton = isNativeIOS();
  const showGoogleButton = !showAppleButton;

  // Hide Emergent badge on auth page
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.display = 'none';
    }
    return () => {
      if (badge) {
        badge.style.display = 'flex';
      }
    };
  }, []);

  // Handle redirect result from OAuth providers (for mobile)
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        console.log('Checking for redirect result...');
        const result = await getRedirectResult(auth);
        
        if (result && result.user) {
          console.log('Got redirect result:', result.user.email);
          setLoading(true);
          
          // Determine provider from providerId
          const providerId = result.providerId || 'unknown';
          const providerName = providerId.includes('apple') ? 'apple' : 
                              providerId.includes('google') ? 'google' : 'oauth';
          
          const firebaseToken = await result.user.getIdToken();
          
          // Send Firebase token to backend
          const response = await axios.post(`${API}/auth/firebase`, {
            firebase_token: firebaseToken,
            provider: providerName
          });
          
          if (response.data.token) {
            toast.success(`Signed in successfully!`);
            const isNewUser = response.data.is_new_user;
            onLogin(response.data.token, !isNewUser); // Skip onboarding for existing users
            navigate(isNewUser ? '/onboarding' : '/zenflow');
          }
        } else {
          console.log('No redirect result found');
        }
      } catch (error) {
        console.error('Redirect result error:', error);
        setAuthError(error.message || 'Authentication failed');
        if (error.code && error.code !== 'auth/popup-closed-by-user') {
          toast.error('Sign-in failed: ' + (error.message || 'Please try again.'));
        }
      } finally {
        setLoading(false);
      }
    };
    
    handleRedirectResult();
  }, [navigate, onLogin]);

  // Don't auto-redirect iOS users - let them choose their sign-in method
  // The Apple button will take them to /auth/apple if they click it

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side guard for clearer feedback before hitting the API.
    if (isSignUp) {
      if (!formData.username.trim()) {
        toast.error('Please enter your name');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
    if (!emailOk) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!requireConsent()) return;

    setLoading(true);

    try {
      let response;
      if (!isSignUp) {
        // Login - existing user, skip onboarding
        response = await axios.post(`${API}/auth/login`, {
          email: formData.email.trim(),
          password: formData.password,
        });
        
        if (response.data.token) {
          toast.success('Welcome back!');
          onLogin(response.data.token, true); // Skip onboarding for existing users
          navigate('/zenflow');
        }
      } else {
        // Signup - new user, show onboarding
        response = await axios.post(`${API}/auth/signup`, {
          email: formData.email.trim(),
          username: formData.username.trim(),
          password: formData.password,
          display_name: formData.full_name.trim() || undefined,
          date_of_birth: formData.date_of_birth || undefined,
          ...buildConsentPayload(),
        });
        
        if (response.data.token) {
          toast.success('Account created!');
          onLogin(response.data.token, false); // Show onboarding for new users
          navigate('/onboarding');
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Authentication failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async (skipConsentGate = false) => {
    // OAuth flows can create new accounts on both login and signup modes.
    // If the user hasn't agreed to Terms / Privacy yet, gate with a modal
    // before initiating any Firebase / native Apple flow.
    // skipConsentGate is passed by the modal's onAccept callback so we don't
    // re-read the (stale) closed-over `agreedToLegal` value after consent.
    console.log('[AppleAuth] button-tap', { skipConsentGate, agreedToLegal, loading, pendingOAuth });
    // `pendingOAuth` only matters as a double-submit guard for a FRESH tap.
    // When the modal's onAccept re-invokes us with skipConsentGate=true, the
    // closure still holds the stale pre-setPendingOAuth(null) value ('apple')
    // and would otherwise swallow the retry entirely — the exact "tap I
    // agree, continue with Apple -> nothing happens" bug.
    if (loading || (!skipConsentGate && pendingOAuth)) return; // hard guard against double-submit
    if (!skipConsentGate && !agreedToLegal) {
      setPendingOAuth('apple');
      return;
    }
    // Native iOS (Capacitor): use the native Apple Sign-In plugin.
    if (isNativePlatform()) {
      console.log('[AppleAuth] button-tap (native)');
      setLoading(true);
      setAuthMethod('apple');
      setAuthError(null);
      try {
        const data = await signInWithAppleNative(buildConsentPayload());
        if (data.token) {
          console.log('[AppleAuth] login-success, navigating', { isNewUser: !!data.is_new_user });
          toast.success('Signed in with Apple successfully!');
          onLogin(data.token, !data.is_new_user);
          navigate(data.is_new_user ? '/onboarding' : '/zenflow');
        } else {
          // Defensive: backend returned 200 without a token — never leave the
          // user on a silent spinner (Apple review Issue 5).
          console.error('[AppleAuth] backend response missing token', data);
          handleAuthError(new Error('Sign-in did not complete. Please try again.'));
        }
      } catch (error) {
        console.error('[AppleAuth] error', { code: error?.code, message: error?.message });
        handleAuthError(error);
      } finally {
        setLoading(false);
        setAuthMethod(null);
      }
      return;
    }
    // Web/desktop: dedicated Apple auth page (Firebase popup).
    console.log('Redirecting to dedicated Apple auth page');
    navigate('/auth/apple');
  };

  const handleGoogleLogin = async (skipConsentGate = false) => {
    // Verbose debug trail — these EXACT strings are documented in the bug
    // template so QA can grep `adb logcat | grep "AUTH DEBUG"` and trace
    // every step of an Android Google login in order.  Don't rename them.
    let platform = 'web';
    try {
      const { Capacitor } = await import('@capacitor/core');
      platform = Capacitor?.getPlatform?.() || 'web';
    } catch {
      platform = '(capacitor not available)';
    }
    if (platform === 'android') {
      console.log('[AUTH DEBUG] Android Google button tapped');
    } else {
      console.log(`[AUTH DEBUG] Google button tapped — platform: ${platform}`);
    }
    console.log('[AUTH DEBUG] handleGoogleLogin', { skipConsentGate, agreedToLegal, loading, pendingOAuth });
    // Same fix as handleAppleLogin: don't let a stale closed-over
    // `pendingOAuth` swallow the modal's onAccept retry (skipConsentGate=true).
    if (loading || (!skipConsentGate && pendingOAuth)) {
      console.log('[AUTH DEBUG] short-circuit — already loading/pending');
      return;
    }
    if (!skipConsentGate && !agreedToLegal) {
      console.log('[AUTH DEBUG] opening legal consent modal');
      setPendingOAuth('google');
      return;
    }
    setLoading(true);
    setAuthMethod('google');
    setAuthError(null);

    try {
      // NATIVE Android / iOS path — MUST run before any web-Firebase check
      // (the web `auth` object can be null on a misconfigured env and an
      // early `!auth` guard would otherwise block the native plugin too —
      // the bug surfaced as "Google Sign-In is temporarily unavailable in
      // this build." in pre-fork builds.  We avoid that entirely by
      // gating purely on `isNativePlatform()`).
      const {
        isNativePlatform,
        isFirebaseAuthPluginAvailable,
        signInWithGoogleNative,
        BUILD_ID,
      } = await import('../lib/googleNative');
      const nativeOn = isNativePlatform();
      const pluginOn = isFirebaseAuthPluginAvailable();
      console.log('[AUTH DEBUG] platform detected', { platform, nativeOn, pluginOn, buildId: BUILD_ID });
      if (nativeOn) {
        console.log('[AUTH DEBUG] native Google plugin available:', pluginOn);
        if (!pluginOn) {
          const pluginErr = new Error(
            'FirebaseAuthentication plugin missing from this build. '
            + 'Rebuild with `yarn build && npx cap sync android` and reinstall the APK.',
          );
          pluginErr.code = 'PLUGIN_NOT_BUNDLED';
          throw pluginErr;
        }
        console.log('[AUTH DEBUG] starting native Google sign-in');
        const native = await signInWithGoogleNative();
        console.log('[AUTH DEBUG] native Google response received', { hasIdToken: !!native?.idToken });
        if (!native?.idToken) {
          const noTokenErr = new Error('No Firebase ID token returned from native Google Sign-In.');
          noTokenErr.code = 'NO_ID_TOKEN';
          throw noTokenErr;
        }
        console.log('[AUTH DEBUG] Firebase credential created');
        const payload = {
          firebase_token: native.idToken,
          provider: 'google',
          ...buildConsentPayload(),
        };
        const response = await axios.post(`${API}/auth/firebase`, payload);
        console.log('[AUTH DEBUG] backend authentication successful', { hasAppToken: !!response.data?.token, isNewUser: !!response.data?.is_new_user });
        if (response.data?.token) {
          // App.js's `handleLogin` (bound to `onLogin`) ALREADY persists the
          // JWT to localStorage as `zenscroll_token` and updates React
          // state.  We also write it here defensively so a partial render
          // failure between `onLogin` and the navigate() call doesn't lose
          // the session — the localStorage key is the source of truth on
          // the next app cold-start.
          const isNewUser = !!response.data.is_new_user;
          localStorage.setItem('zenscroll_token', response.data.token);
          console.log('[AUTH DEBUG] calling onLogin(token)', { isNewUser });
          onLogin(response.data.token, !isNewUser); // skip onboarding for existing users
          toast.success('Signed in with Google successfully!');
          navigate(isNewUser ? '/onboarding' : '/zenflow');
        }
        return;
      }

      // WEB / browser path — popup is the right choice here, Capacitor
      // builds already returned above.
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      console.log('[AUTH DEBUG] starting web signInWithPopup(google)');
      const result = await signInWithPopup(auth, provider);
      console.log('[AUTH DEBUG] signInWithPopup returned uid:', result?.user?.uid);
      await handleFirebaseAuthResult(result, 'google');
    } catch (error) {
      console.error('[AUTH DEBUG] native Google sign-in error', {
        code: error?.code,
        message: error?.message,
        native: error?.errorMessage,
      });
      setAuthError(error.message || 'Google Sign-In failed');
      handleAuthError(error);
    } finally {
      setLoading(false);
      setAuthMethod(null);
    }
  };

  const handleFirebaseAuthResult = async (result, providerName) => {
    try {
      const firebaseToken = await result.user.getIdToken();

      // Send Firebase token to backend. Always send consent metadata —
      // the backend ignores it for existing users but records it the first
      // time we see this account. The legal-consent modal gates all OAuth
      // attempts so by the time we reach this line, consent is granted.
      const payload = {
        firebase_token: firebaseToken,
        provider: providerName,
        ...buildConsentPayload(),
      };
      const response = await axios.post(`${API}/auth/firebase`, payload);
      
      if (response.data.token) {
        toast.success(`Signed in with ${providerName === 'apple' ? 'Apple' : 'Google'} successfully!`);
        const isNewUser = response.data.is_new_user;
        onLogin(response.data.token, !isNewUser); // Skip onboarding for existing users
        navigate(isNewUser ? '/onboarding' : '/zenflow');
      }
    } catch (backendError) {
      console.error('Backend auth error:', backendError);
      toast.error('Authentication failed. Please try again.');
    }
  };

  const handleAuthError = (error) => {
    setLoading(false);
    // User dismissed the native Apple sheet / OAuth popup — that's not an
    // error. Reset quietly so the button is immediately tappable again
    // (Apple review: cancel must not show a misleading fatal error).
    const cancelRaw = `${error?.code || ''} ${error?.message || ''}`.toLowerCase();
    if (
      cancelRaw.includes('1001') ||
      cancelRaw.includes('cancel') ||
      cancelRaw.includes('popup-closed-by-user') ||
      cancelRaw.includes('user-cancelled') ||
      cancelRaw.includes('abort')
    ) {
      setAuthMethod(null);
      setAuthError(null);
      return;
    }
    let errorMessage = 'Sign-in failed. Please try again.';

    // Android-native Google Sign-In specific error codes — surfaced from
    // `@capacitor-firebase/authentication`. The most common one in this
    // codebase is `12500` / `DEVELOPER_ERROR`, which means the Firebase
    // project doesn't know about this build's signing-certificate SHA-1.
    // Fix: add the debug + release SHA-1 fingerprints to the Firebase
    // Console Android app, redownload google-services.json, run
    // `npx cap sync android`, then rebuild.
    const raw = `${error?.code || ''} ${error?.message || ''}`.toLowerCase();
    const isAndroidConfigError =
      raw.includes('12500') ||
      raw.includes('developer_error') ||
      raw.includes('developererror') ||
      raw.includes('sha-1') ||
      raw.includes('sha1') ||
      raw.includes('apikeynotvalid') ||
      raw.includes('app_not_authorized');
    const isPluginUnavailable =
      error?.code === 'UNAVAILABLE' ||
      error?.code === 'PLUGIN_NOT_BUNDLED' ||
      raw.includes('plugin is not implemented') ||
      raw.includes('not bundled') ||
      raw.includes('not available');
    const isNativeTimeout =
      error?.code === 'NATIVE_TIMEOUT' ||
      raw.includes('did not respond');

    if (isNativeTimeout) {
      errorMessage = 'Apple Sign-In did not respond. Please try again.';
    } else if (isAndroidConfigError) {
      errorMessage =
        "Google Sign-In isn't configured for this Android build yet. " +
        'Add the build\u2019s SHA-1 + SHA-256 fingerprints in Firebase Console, ' +
        're-download google-services.json, and rebuild.';
    } else if (isPluginUnavailable) {
      errorMessage =
        'Sign-in is temporarily unavailable in this build. Please update the app or use email sign-in below.';
    } else if (error.code === 'auth/popup-closed-by-user' || error.code === '12501' || raw.includes('canceled')) {
      errorMessage = 'Sign-in cancelled';
    } else if (error.code === 'auth/popup-blocked') {
      errorMessage = 'Popup blocked. Please allow popups or try again.';
    } else if (error.code === 'auth/unauthorized-domain') {
      errorMessage = 'This domain is not authorized for sign-in. Please contact support.';
    } else if (error.code === 'auth/operation-not-allowed') {
      errorMessage = 'This sign-in method is not enabled. Please contact support.';
    } else if (error.code === 'auth/network-request-failed' || raw.includes('network')) {
      errorMessage = 'Network error. Please check your connection.';
    } else if (error.message) {
      errorMessage =
        `${error.message}. If this keeps happening, please try again, `
        + 'use email sign-in, or contact support.';
    }

    console.error('[AUTH DEBUG] mapped error', { code: error?.code, message: error?.message, shown: errorMessage });
    toast.error(errorMessage);
    setAuthError(errorMessage);
  };

  // Show loading screen when signing in
  if (loading && (authMethod === 'apple' || authMethod === 'google')) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-x-hidden bg-[#0A1514]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-sm relative z-10"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <LogoWithText iconSize="w-10 h-10" textSize="text-3xl" />
          </div>
          <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-4" />
          <p className="text-white/80 mb-2">
            Connecting to {authMethod === 'apple' ? 'Apple' : 'Google'} Sign-In...
          </p>
          <p className="text-xs text-white/60">
            You may be redirected to sign in
          </p>
          
          {authError && (
            <div className="mt-6 p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl">
              <div className="flex items-center gap-2 text-red-300 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium text-sm">Error</span>
              </div>
              <p className="text-sm text-white/80">{authError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setLoading(false);
                  setAuthError(null);
                  navigate('/auth');
                }}
                className="mt-3 rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                Try Again
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0A1514] text-white relative overflow-x-hidden flex items-center justify-center px-4 py-6 sm:py-8"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <GrainOverlay />
      <Glow className="-top-32 -left-32 w-[520px] h-[520px]" color="#1C4749" opacity={0.18} />
      <Glow className="top-1/2 -right-40 w-[420px] h-[420px]" color="#FFD166" opacity={0.06} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center">
          <div className="flex items-center justify-center">
            <img
              src={ZENSCROLL_LOGO}
              alt="ZenScroll"
              className="w-28 sm:w-32 h-auto"
            />
          </div>
          <h1
            data-testid="auth-welcome-heading"
            className="mt-1 text-xl sm:text-2xl text-white tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Welcome to ZenScroll
          </h1>
          <p
            data-testid="auth-welcome-tagline"
            className="mt-1 text-xs sm:text-sm text-[#94A3B8]"
          >
            Create, connect, and scroll better.
          </p>
        </div>

        <div data-testid="auth-form" className="mt-4 bg-[#101F1D]/60 backdrop-blur-xl rounded-3xl border border-white/10 p-5 sm:p-6 shadow-[0_30px_60px_rgba(0,0,0,0.4)]">
          {showDiag && (
            <div
              data-testid="auth-diagnostics-panel"
              className="mb-4 p-3 rounded-xl border border-amber-400/40 bg-amber-400/5 text-[11px] text-amber-100/90 font-mono"
            >
              <div className="text-amber-300 font-semibold mb-1">Auth diagnostics</div>
              <pre className="whitespace-pre-wrap break-all">
                {diagInfo ? JSON.stringify(diagInfo, null, 2) : 'loading...'}
              </pre>
            </div>
          )}
          {/* Social Sign In Buttons */}
          <div className="space-y-2 mb-4">
            {/* Google Sign In Button — hidden on native iOS */}
            {showGoogleButton && (
            <button
              data-testid="google-login-btn"
              data-auth="google"
              onClick={() => handleGoogleLogin()}
              disabled={loading || pendingOAuth !== null}
              className="google-login-button w-full py-3 px-4 rounded-xl bg-white text-gray-800 font-medium text-sm flex items-center justify-center gap-2 border border-white/20 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative z-20"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto' }}
              type="button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Signing in...' : 'Continue with Google'}
            </button>
            )}

            {/* Apple Sign In Button — shown only on native iOS */}
            {showAppleButton && (
            <button
              data-testid="apple-login-btn"
              onClick={() => handleAppleLogin()}
              disabled={loading || pendingOAuth !== null}
              className="w-full py-3 px-4 rounded-xl bg-black text-white font-medium text-sm flex items-center justify-center gap-2 border border-white/10 hover:bg-gray-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              {loading ? 'Signing in...' : 'Continue with Apple'}
            </button>
            )}
          </div>

          {/* Show form for both Sign In and Sign Up modes */}
          <form onSubmit={handleSubmit} className="space-y-3 mt-4">
            {/* Show Name fields only for Sign Up */}
            {isSignUp && (
              <>
                <div>
                  <Label htmlFor="full_name" className="text-white/80">Full name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-3 w-5 h-5 text-white/40" />
                    <Input
                      data-testid="input-full-name"
                      id="full_name"
                      type="text"
                      placeholder="Jane Smith"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="pl-10 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#1C4749]"
                      required={isSignUp}
                      autoComplete="name"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="username" className="text-white/80">Username</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-3 w-5 h-5 text-white/40" />
                    <Input
                      data-testid="input-username"
                      id="username"
                      type="text"
                      placeholder="janesmith"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="pl-10 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#1C4749]"
                      required={isSignUp}
                      autoComplete="username"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-white/40" />
                <Input
                  data-testid="input-email"
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#1C4749]"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-white/40" />
                <Input
                  data-testid="input-password"
                  id="password"
                  type="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#1C4749]"
                  required
                />
              </div>
            </div>

            {isSignUp && (
              <div>
                <Label htmlFor="date_of_birth" className="text-white/80">Date of birth</Label>
                <div className="relative mt-1">
                  <Input
                    data-testid="input-date-of-birth"
                    id="date_of_birth"
                    type="date"
                    max={new Date(new Date().getFullYear() - 13, new Date().getMonth(), new Date().getDate()).toISOString().slice(0, 10)}
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#1C4749] [color-scheme:dark]"
                    required={isSignUp}
                  />
                </div>
                <p className="text-[11px] text-white/50 mt-1">You must be at least 13 to sign up.</p>
              </div>
            )}

            {!isSignUp && (
              <div className="flex flex-wrap items-center justify-between gap-2 -mt-1">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => setRecoverPwdOpen(true)}
                  className="text-xs text-[#5EEAD4] hover:text-[#5EEAD4]/80 hover:underline transition-colors"
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  data-testid="forgot-username-link"
                  onClick={() => setRecoverUserOpen(true)}
                  className="text-xs text-white/60 hover:text-white hover:underline transition-colors"
                >
                  Forgot username or email?
                </button>
              </div>
            )}

            <button
              data-testid="submit-btn"
              type="submit"
              disabled={loading || (isSignUp && !agreedToLegal)}
              className="w-full py-3 px-4 rounded-xl bg-[#1C4749] text-white font-semibold text-sm flex items-center justify-center hover:bg-[#2A6F72] transition-all duration-200 disabled:opacity-50 mt-4 shadow-[0_0_24px_rgba(28,71,73,0.35)] hover:shadow-[0_0_32px_rgba(28,71,73,0.5)]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isSignUp ? 'Creating account...' : 'Signing in...'}
                </>
              ) : (
                isSignUp ? 'Sign Up' : 'Sign In'
              )}
            </button>

            {isSignUp && (
              <label
                data-testid="legal-consent-row"
                className="mt-4 flex items-start gap-2.5 text-xs text-white/70 cursor-pointer select-none"
              >
                <input
                  data-testid="legal-consent-checkbox"
                  type="checkbox"
                  required
                  checked={agreedToLegal}
                  onChange={(e) => setAgreedToLegal(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-white/30 bg-white/10 text-[#1C4749] focus:ring-[#1C4749]"
                />
                <span className="leading-snug">
                  I agree to the{' '}
                  <button
                    type="button"
                    data-testid="legal-consent-terms-link"
                    onClick={() => navigate('/terms')}
                    className="text-[#FFD166] underline hover:text-[#FFD166]/80"
                  >
                    Terms of Service
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    data-testid="legal-consent-privacy-link"
                    onClick={() => navigate('/privacy')}
                    className="text-[#FFD166] underline hover:text-[#FFD166]/80"
                  >
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
            )}
          </form>
        </div>

        <div className="mt-3 text-center">
          <Button
            data-testid="back-to-landing-btn"
            variant="link"
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white text-sm h-auto py-1 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" aria-hidden />
            Back to home
          </Button>
        </div>

        <div className="mt-1 flex items-center justify-center gap-2 text-xs text-white/50">
          <button
            type="button"
            data-testid="auth-link-privacy"
            onClick={() => navigate('/privacy')}
            className="hover:text-white underline-offset-2 hover:underline"
          >
            Privacy Policy
          </button>
          <span aria-hidden className="inline-block w-1 h-1 rounded-full bg-white/40" />
          <button
            type="button"
            data-testid="auth-link-terms"
            onClick={() => navigate('/terms')}
            className="hover:text-white underline-offset-2 hover:underline"
          >
            Terms of Service
          </button>
        </div>
      </motion.div>

      <LegalConsentModal
        open={pendingOAuth !== null}
        provider={pendingOAuth}
        onCancel={() => setPendingOAuth(null)}
        onAccept={() => {
          const which = pendingOAuth;
          setAgreedToLegal(true);
          setPendingOAuth(null);
          // Defer to the next tick AND pass skipConsentGate=true so the
          // re-fired handler doesn't read the stale closed-over
          // `agreedToLegal` value from the previous render.
          setTimeout(() => {
            if (which === 'google') handleGoogleLogin(true);
            else if (which === 'apple') handleAppleLogin(true);
          }, 0);
        }}
        onOpenTerms={() => navigate('/terms')}
        onOpenPrivacy={() => navigate('/privacy')}
      />

      {/* Account-recovery dialogs (mounted on the Sign In screen only —
          `setRecoverPwdOpen` / `setRecoverUserOpen` are only triggered when
          isSignUp is false). */}
      <ForgotPasswordDialog
        open={recoverPwdOpen}
        onClose={() => setRecoverPwdOpen(false)}
        initialEmail={formData.email}
      />
      <ForgotUsernameHelpDialog
        open={recoverUserOpen}
        onClose={() => setRecoverUserOpen(false)}
      />
    </div>
  );
}

function LegalConsentModal({ open, provider, onCancel, onAccept, onOpenTerms, onOpenPrivacy }) {
  // Track when the backdrop first became interactive.  Android Capacitor
  // WebViews synthesise a `click` event ~300ms after a touch, which used
  // to fire on this backdrop the instant it mounted and silently dismissed
  // the modal — the "Continue with Google" button looked unresponsive.
  // Ignoring backdrop clicks for the first ~450ms eliminates the ghost
  // tap.  The explicit Cancel button still closes the modal immediately.
  const mountedAtRef = useRef(0);
  useEffect(() => {
    if (open) mountedAtRef.current = Date.now();
  }, [open]);
  if (!open) return null;
  const label = provider === 'google' ? 'Google' : provider === 'apple' ? 'Apple' : 'this provider';
  const handleBackdropClick = () => {
    if (Date.now() - mountedAtRef.current < 450) return;
    onCancel();
  };
  return (
    <div
      data-testid="legal-consent-modal"
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white border border-gray-200 shadow-2xl p-7"
      >
        <h3
          className="text-xl text-gray-900 tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Continue with {label}
        </h3>
        <p className="mt-3 text-sm text-gray-700 leading-relaxed">
          To create or access your ZenScroll account, please confirm you agree to our terms.
        </p>
        <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-800 leading-relaxed">
          I agree to the{' '}
          <button
            type="button"
            data-testid="legal-consent-modal-terms-link"
            onClick={onOpenTerms}
            className="text-primary underline hover:text-primary/80"
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <button
            type="button"
            data-testid="legal-consent-modal-privacy-link"
            onClick={onOpenPrivacy}
            className="text-primary underline hover:text-primary/80"
          >
            Privacy Policy
          </button>
          .
        </div>
        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 justify-end">
          <button
            type="button"
            data-testid="legal-consent-modal-cancel"
            onClick={onCancel}
            className="rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="legal-consent-modal-accept"
            onClick={onAccept}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary/90"
          >
            I agree, continue with {label}
          </button>
        </div>
      </div>
    </div>
  );
}
